/**
 * Payment history composition — the one rule for how a payment the customer made
 * THIS SESSION becomes a row on a Payment History surface.
 *
 * Why this is shared rather than written twice: D-3 states the contract over
 * *every* surface that lists payments, and both Payment History pages missed it
 * in the same way (F-C2) — they read `FIXTURE_PAYMENTS` only, so a customer who
 * had just paid saw their own confirmation number on Home and in the batch-pay
 * strip but NOT on the page literally titled Payment History. Two surfaces
 * disagreeing about whether a payment exists is the coherence-class-5 defect the
 * cross-surface parity contract exists to make impossible by construction. If web
 * and mobile each mapped the ledger entry themselves, the same divergence would
 * simply move up one layer — a differently-formatted method, a different ref
 * column, a different sort.
 *
 * Split pure-function / no-React on the `resolveBillState` precedent: shared-ui
 * runs vitest under `environment: "node"`, so keeping this a plain function is
 * what makes the rule directly testable rather than only source-contract pinned.
 */
import type { PaymentEntry } from "./demo-store";
import type { PaymentRecord } from "./payment-history";

/**
 * The persona's method on file. Structurally minimal on purpose (ISP): the
 * composition needs the brand and the masked PAN and nothing else, so it accepts
 * any `storedPayment` without depending on `expiry`.
 */
export interface StoredMethodSummary {
  brand: string;
  last4: string;
}

/**
 * Shown where a method is expected and the persona has none. An em dash rather
 * than a fabricated card: web's confirm summary already reads "—" in that branch,
 * and inventing an instrument is exactly the F-C1 defect.
 */
export const NO_METHOD_LABEL = "—";

/**
 * The customer's method as one display string — `Visa •••• 6411`, or the
 * no-method dash. One formatter so the pay flow, the confirm summary and the
 * history table cannot render the same card three ways.
 */
export function formatStoredMethod(method: StoredMethodSummary | undefined): string {
  return method ? `${method.brand} •••• ${method.last4}` : NO_METHOD_LABEL;
}

/**
 * This session's payments as history rows, newest first, above the seeded rows.
 *
 * Decisions locked by the 2026-07-27 CHECK §4 and applied identically on both
 * surfaces:
 *  - **date** is `dateIso` — the day the customer actually paid (local, per the
 *    store's `todayIsoLocal`), never the bill's due date.
 *  - **method** resolves from the persona at RENDER time. Truthful because the
 *    flow only ever charges the method on file, and once the amount step gates on
 *    method presence a persona without one cannot produce a session payment.
 *  - **ref** is the confirmation number, so the row ties back to the receipt the
 *    success screen printed.
 *  - **status** is `confirmed` — the payment settled in-session; there is no
 *    pending state to model in demo-ware.
 *
 * @param sessionPayments - the persona's ledger entries, newest first.
 * @param fixtureRows - seeded history, already range-filtered by the caller.
 *   Session rows are today's by construction and are included unconditionally,
 *   so a 3/6/12-month filter can never hide the payment just made.
 */
export function mergePaymentHistory(
  sessionPayments: readonly PaymentEntry[],
  fixtureRows: readonly PaymentRecord[],
  storedPayment: StoredMethodSummary | undefined,
): PaymentRecord[] {
  const method = formatStoredMethod(storedPayment);

  const sessionRows: PaymentRecord[] = sessionPayments.map((p) => ({
    id: p.confirmation,
    amount: p.amount,
    method,
    date: p.dateIso,
    status: "confirmed",
  }));

  return [...sessionRows, ...fixtureRows];
}
